import React from 'react';
import CoursePage from '../Learnings/Courses/CoursePage';
import CourseDetailPage from '../Learnings/Courses/[id]/id';
import { usePageContainer } from '../../hooks/usePageContainer';
import PageContainerLayout from '../../components/layout/PageContainer/PageContainerLayout';
import StudyMaterial from './StudyMaterial/StudyMaterial';
import MaterialDetailPage from './StudyMaterial/MaterialDetailPage';
import StudyMaterialRightComponent from './StudyMaterial/StudyMaterialRightComponent';
import { useSearchParams, useLocation } from 'react-router-dom';
import useCourseDetails from '../../hooks/useCourseDetails';
import Button from '../../components/reusableComponents/buttons/button11';

// Import all learning-related icons
import coursesIcon from '../../assets/learnings/courses/header/Courses.svg';
import assignmentsIcon from '../../assets/learnings/courses/header/Assignments.svg';
import examsIcon from '../../assets/learnings/courses/header/Exams.svg';
import quizIcon from '../../assets/learnings/courses/header/Courses.svg';
import materialsIcon from '../../assets/learnings/courses/header/StudyMaterials.svg';
import timetableIcon from '../../assets/learnings/courses/header/Timetable.svg';

const LearningPageContainer = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const courseId = searchParams.get('courseId');
  const batchId = searchParams.get('batchId');
  
  // Check if we're on a material detail page
  const materialId = searchParams.get('id');
  const materialType = searchParams.get('type');
  const isMaterialDetail = Boolean(materialId && materialType);
  
  // Get material name from location state
  const materialName = location.state?.materialName || 'Study Material';

  // Always call the hook unconditionally
  const { courseDetails, isLoading, error } = useCourseDetails(courseId, batchId);

  // Check if we're on a course detail page
  const isCoursePage = Boolean(courseId && batchId);

  // Define page configuration
  const learningPages = {
    'courses': { component: <CoursePage />, icon: coursesIcon },
    'assignments': { component: <div>Assignments Content</div>, icon: assignmentsIcon },
    'exams': { component: <div>Exams Content</div>, icon: examsIcon },
    'quiz': { component: <div>Quiz Content</div>, icon: quizIcon },
    'materials': { component: <div><StudyMaterial/></div>, icon: materialsIcon },
    'material': { component: <MaterialDetailPage />, icon: materialsIcon }, // Add material detail page
    'timetable': { component: <div>TimeTable Content</div>, icon: timetableIcon },
    'course': {
      component: <CourseDetailPage />,
      icon: coursesIcon
    }
  };

  const learningTitles = {
    'courses': 'Courses',
    'assignments': 'Assignments',
    'exams': 'Exams',
    'quiz': 'Quiz',
    'materials': 'Study Materials',
    'material': materialName, // Use the material name as title
    'timetable': 'Timetable',
    'learning': 'Learning',
    'course': courseDetails?.courseName || 'Course Details'
  };

  // Custom breadcrumb factory for learning pages
  const learningBreadcrumbFactory = (basePath, currentPage, pathSegments) => {
    const breadcrumbs = [
      { label: 'Home', path: '/dashboard', active: false },
      { label: 'Learning', path: '/learning/courses', active: false }
    ];

    if (currentPage === 'course' && courseDetails?.courseName) {
      // Course breadcrumbs
      breadcrumbs.push({
        label: 'Courses',
        path: '/learning/courses',
        active: false
      });

      breadcrumbs.push({
        label: courseDetails.courseName,
        path: `/learning/courses/course?courseId=${courseId}&batchId=${batchId}`,
        active: true
      });
    } else if (currentPage === 'material' && isMaterialDetail) {
      // Material detail breadcrumbs
      breadcrumbs.push({
        label: 'Study Materials',
        path: '/learning/materials',
        active: false
      });
      
      breadcrumbs.push({
        label: materialName,
        path: `/learning/materials/material?id=${materialId}&type=${materialType}`,
        active: true
      });
    } else if (currentPage !== 'courses') {
      // Regular page breadcrumbs
      breadcrumbs.push({
        label: learningTitles[currentPage],
        path: `/learning/${currentPage}`,
        active: true
      });
    } else {
      // Just courses
      breadcrumbs.push({
        label: 'Courses',
        path: `/learning/courses`,
        active: true
      });
    }

    return breadcrumbs;
  };

  // Use the centralized hook
  const {
    pageComponent,
    pageIcon,
    pageTitle,
    breadcrumbs,
    currentPage
  } = usePageContainer({
    basePath: 'learning',
    defaultPage: 'courses',
    pages: learningPages,
    pageTitles: learningTitles,
    breadcrumbFactory: learningBreadcrumbFactory
  });

  // Button to display in the header for course detail pages
  const getHeaderRightContent = () => {
    if (isCoursePage) {
      return (
        <Button
          text="Resume Course"
          onClick={() => {
            console.log(`Resuming course: ${courseDetails.courseName}`);
          }}
          state="Disabled"
        />
      );
    }

    if (currentPage === 'materials') {
      return null; // or return <StudyMaterialRightComponent /> if you want to show it here
    }
        
    return null;
  };

  // Show loading state for course detail page
  if (isCoursePage && isLoading) {
    return (
      <PageContainerLayout
        title="Loading..."
        icon={coursesIcon}
        breadcrumbs={[
          { label: 'Home', path: '/dashboard', active: false },
          { label: 'Learning', path: '/learning/courses', active: false },
          { label: 'Courses', path: '/learning/courses', active: false },
          { label: 'Loading...', path: '#', active: true }
        ]}
      >
        <div className="w-full p-8 flex justify-center">
          <div className="animate-pulse text-lg">Loading course details...</div>
        </div>
      </PageContainerLayout>
    );
  }

  return (
    <PageContainerLayout
      title={pageTitle}
      icon={pageIcon}
      breadcrumbs={breadcrumbs}
      rightContent={getHeaderRightContent()}
      showIcon={!isCoursePage} // Hide icon on detail pages
    >
      {pageComponent}
    </PageContainerLayout>
  );
};

export default LearningPageContainer;